import { ChatWithFeedbackContainer } from '@modules/chatWithFeedback/components/ChatWithFeedbackContainer';
import { SearchAnalysesFeedbackList } from '@modules/feed/components/SearchAnalysesFeedbackList';
import { SearchSummaryContent } from '@modules/feed/components/SearchSummaryContent';
import { useIsChatWithFeedbackEnabled } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { trackSearchSummaryEvent } from '@modules/feed/utils/trackSearchSummaryEvents';
import { GenerateSummaryLoader } from '@modules/oracle/components/GenerateSummaryLoader';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import {
  useGetFeedbackListSummaryByQueryContainer,
  useStopAssistantResponse,
} from '@modules/oracle/hooks';
import { Box, Slide, Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { QueryContainer } from 'v2/core/services/notebook/types';

interface SearchSummaryAndFeedbackListProps {
  queryContainer: QueryContainer;
}

export const SearchSummaryAndFeedbackList = ({
  queryContainer,
}: SearchSummaryAndFeedbackListProps) => {
  const {
    responseID,
    getFeedbackSummary,
    sessionID: isSocketConnectionEstablished,
    error,
  } = useGetFeedbackListSummaryByQueryContainer({
    queryContainer,
    shouldGetSummaryOnDemand: true,
  });

  const [stopResponse, { isLoading: isStopResponseInProgress }] =
    useStopAssistantResponse();

  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();
  const dispatch = useFeedStore((state) => state.dispatch);
  const defaultPrompt = useFeedStore((state) => state.prompt);

  const feedbackSummaryDispatch = useFeedbackSummaryStore(
    ({ dispatch }) => dispatch,
  );

  const shouldShowSummary = useFeedStore(
    ({ visual }) => visual.shouldShowSummary,
  );
  const [isGenerateActionPerformed, setIsGenerateActionPerformed] =
    useState(false);

  const [isAskOrSummarizeCTAClicked, setIsAskOrSummarizeCTAClicked] =
    useState(false);

  const slideContainerRef = useRef<HTMLDivElement>(null);

  const isCompleted = useFeedbackSummaryStore(
    ({ visual }) => visual.isCompleted,
  );
  const websocketError = useFeedbackSummaryStore(({ visual }) => visual.error);
  const messages = useFeedbackSummaryStore(({ data }) => data.messages);

  useEffect(() => {
    if (
      isChatWithFeedbackEnabled &&
      !isSocketConnectionEstablished &&
      messages.length > 0 &&
      !isCompleted
    ) {
      feedbackSummaryDispatch({
        type: FeedbackSummaryActionType.RESET,
      });
    }
  }, [
    feedbackSummaryDispatch,
    isChatWithFeedbackEnabled,
    isCompleted,
    isSocketConnectionEstablished,
    messages.length,
  ]);

  const generateSummary = useCallback(
    (prompt?: string) => {
      setIsGenerateActionPerformed(true);
      getFeedbackSummary(prompt);

      if (prompt) {
        feedbackSummaryDispatch({
          type: FeedbackSummaryActionType.RESET,
        });

        feedbackSummaryDispatch({
          type: FeedbackSummaryActionType.SET_VISUAL,
          payload: {
            shouldRejectIncomingMessages: false,
          },
        });

        dispatch({
          type: SearchActionType.SET_PROMPT,
          payload: prompt,
        });

        if (prompt !== defaultPrompt) {
          dispatch({
            type: SearchActionType.SET_VISUAL,
            payload: {
              hasChangesToBeSaved: true,
            },
          });
        }
      }
    },
    [getFeedbackSummary, feedbackSummaryDispatch, dispatch, defaultPrompt],
  );

  const onAskOrSummarizeButtonClick = () => {
    setIsAskOrSummarizeCTAClicked(true);

    dispatch({
      type: SearchActionType.SET_VISUAL,
      payload: { shouldShowSummary: true },
    });

    if (isChatWithFeedbackEnabled) {
      feedbackSummaryDispatch({
        type: FeedbackSummaryActionType.RESET,
      });
      return;
    }

    generateSummary();

    trackSearchSummaryEvent({
      eventName: 'Search Summary - Generate Summary',
    });
  };

  const onStopSummaryGeneration = useCallback(async () => {
    if (!responseID) {
      return;
    }

    await stopResponse(responseID);

    feedbackSummaryDispatch({
      type: FeedbackSummaryActionType.RESET,
    });

    feedbackSummaryDispatch({
      type: FeedbackSummaryActionType.SET_VISUAL,
      payload: {
        shouldRejectIncomingMessages: true,
      },
    });

    setIsGenerateActionPerformed(false);
  }, [feedbackSummaryDispatch, responseID, stopResponse]);

  const onClose = useCallback(() => {
    if (responseID) {
      stopResponse(responseID);
    }

    setIsGenerateActionPerformed(false);

    feedbackSummaryDispatch({
      type: FeedbackSummaryActionType.RESET,
    });
    dispatch({
      type: SearchActionType.SET_VISUAL,
      payload: { shouldShowSummary: false },
    });
    dispatch({
      type: SearchActionType.SET_PROMPT,
      payload: '',
    });

    dispatch({
      type: SearchActionType.SET_VISUAL,
      payload: {
        hasChangesToBeSaved: true,
      },
    });
  }, [dispatch, feedbackSummaryDispatch, responseID, stopResponse]);

  const isError = Boolean(error) || Boolean(websocketError);

  const isLoading =
    !isCompleted &&
    messages.length === 0 &&
    isGenerateActionPerformed &&
    !isError;

  const hasNoSummary = messages.length === 0 && isCompleted;

  const isSummaryGenerationInProgress =
    !isCompleted &&
    messages.length > 0 &&
    isGenerateActionPerformed &&
    !isError;

  const renderSummaryContent = useCallback(() => {
    if (isChatWithFeedbackEnabled && (shouldShowSummary || defaultPrompt)) {
      return (
        <Box width="100%">
          <ChatWithFeedbackContainer
            onClose={onClose}
            onSubmit={(prompt) => generateSummary(prompt)}
            isError={isError}
            isLoading={isLoading}
            hasNoSummary={hasNoSummary}
            defaultPrompt={defaultPrompt}
            isSummaryGenerationInProgress={isSummaryGenerationInProgress}
            onStopSummaryGeneration={onStopSummaryGeneration}
            isStopResponseInProgress={isStopResponseInProgress}
            responseID={responseID}
            queryContainer={queryContainer}
            onRegenerateSummary={() => generateSummary(defaultPrompt)}
            isGenerateActionPerformed={isGenerateActionPerformed}
            isSocketConnectionEstablished={Boolean(
              isSocketConnectionEstablished,
            )}
          />
        </Box>
      );
    }

    if (isChatWithFeedbackEnabled) {
      return null;
    }

    if (!isCompleted && messages.length === 0) {
      return (
        <GenerateSummaryLoader
          containerSxProps={{
            px: 12,
            py: 2,
            border: (theme) =>
              `1px solid ${theme.palette.misc?.wisdom.summary.borderColor}`,
          }}
        />
      );
    }

    return (
      <SearchSummaryContent
        hasNoSummary={hasNoSummary}
        responseID={responseID}
        queryContainer={queryContainer}
      />
    );
  }, [
    isChatWithFeedbackEnabled,
    shouldShowSummary,
    defaultPrompt,
    isCompleted,
    messages.length,
    hasNoSummary,
    responseID,
    queryContainer,
    onClose,
    isLoading,
    isSummaryGenerationInProgress,
    onStopSummaryGeneration,
    isStopResponseInProgress,
    isGenerateActionPerformed,
    isSocketConnectionEstablished,
    generateSummary,
    isError,
  ]);

  const isAskOrSummarizeButtonVisible = () => {
    if (shouldShowSummary) {
      return false;
    }

    if (defaultPrompt) {
      return false;
    }

    if (isGenerateActionPerformed) {
      return false;
    }

    return true;
  };

  const getShouldShowSummary = () => {
    if (defaultPrompt) {
      return true;
    }

    if (shouldShowSummary) {
      return true;
    }

    if (isGenerateActionPerformed) {
      return true;
    }

    return false;
  };

  // Auto generate summary if the button is not visible
  useEffect(
    function autoGenerateSummary() {
      if (
        isGenerateActionPerformed ||
        !shouldShowSummary ||
        isChatWithFeedbackEnabled
      ) {
        return;
      }

      if (!isSocketConnectionEstablished) {
        const timer = setTimeout(autoGenerateSummary, 200);

        return () => {
          clearTimeout(timer);
        };
      }

      generateSummary();
    },
    [
      generateSummary,
      isSocketConnectionEstablished,
      isGenerateActionPerformed,
      shouldShowSummary,
      isChatWithFeedbackEnabled,
    ],
  );

  return (
    <>
      {getShouldShowSummary() && (
        <Stack
          overflow="hidden"
          sx={{
            boxShadow: isCompleted
              ? `0px 0px 30px 2px rgba(191, 194, 255, 0.1)`
              : 'initial',
          }}
        >
          <Slide
            direction="down"
            in
            timeout={isAskOrSummarizeCTAClicked ? 500 : 0}
            container={slideContainerRef.current}
          >
            <Box>{renderSummaryContent()}</Box>
          </Slide>
        </Stack>
      )}
      <SearchAnalysesFeedbackList
        askOrSummarizeCTAProps={{
          isLoading: !isSocketConnectionEstablished,
          isVisible: isAskOrSummarizeButtonVisible(),
          onClick: onAskOrSummarizeButtonClick,
        }}
      />
    </>
  );
};
